import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Gauge, Recycle, Lightning, ShieldCheck, Timer, ChartLineUp } from "@phosphor-icons/react";

function KpiTile({ icon: Icon, label, value, unit, tone = "default", sub, testId }) {
  const toneMap = {
    default: "text-foreground",
    good: "text-emerald-600", warn: "text-amber-600", bad: "text-red-600", primary: "text-primary",
  };
  return (
    <Card className="shadow-none" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon size={13} className="text-muted-foreground"/>
          <div className="overline">{label}</div>
        </div>
        <div className={`text-metric text-3xl ${toneMap[tone]}`}>
          {value}<span className="text-base text-muted-foreground ml-0.5">{unit}</span>
        </div>
        {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function EnhancedKpisStrip() {
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/kpis/enhanced").then(r => setK(r.data)).catch(()=>{}); }, []);
  if (!k) return null;

  const oeeTone = k.oee >= 75 ? "good" : k.oee >= 50 ? "warn" : "bad";
  const wastageTone = k.wastage_pct <= 2 ? "good" : k.wastage_pct <= 5 ? "warn" : "bad";
  const qualityTone = k.quality_pct >= 95 ? "good" : k.quality_pct >= 85 ? "warn" : "bad";

  return (
    <div className="mb-6" data-testid="enhanced-kpis">
      <div className="flex items-center justify-between mb-2">
        <div className="overline">World-class KPIs</div>
        <div className="text-[10px] text-muted-foreground mono">A × P × Q → OEE</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Card className="shadow-none col-span-2 lg:col-span-2" data-testid="kpi-oee">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Gauge size={13} className="text-muted-foreground"/>
              <div className="overline">Overall Equipment Effectiveness</div>
            </div>
            <div className={`text-metric text-5xl ${
              oeeTone === "good" ? "text-emerald-600" : oeeTone === "warn" ? "text-amber-600" : "text-red-600"
            }`}>
              {k.oee}<span className="text-xl text-muted-foreground">%</span>
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px]">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-500"/>Avail <b className="mono">{k.availability_pct}%</b>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500"/>Perf <b className="mono">{k.performance_pct}%</b>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>Qual <b className="mono">{k.quality_pct}%</b>
              </span>
            </div>
            <Progress value={k.oee} className="h-1.5 mt-2"/>
          </CardContent>
        </Card>
        <KpiTile icon={Recycle} label="Wastage" value={k.wastage_pct} unit="%"
                 sub={`${k.wastage_kg} kg rejected`} tone={wastageTone} testId="kpi-wastage"/>
        <KpiTile icon={Lightning} label="Power" value={k.power_kwh} unit=" kWh"
                 sub="Today's freeze-dryer draw" tone="primary" testId="kpi-power"/>
        <KpiTile icon={ShieldCheck} label="QC pass rate" value={k.qc_pass_rate} unit="%"
                 sub="Passed / decided today" tone={qualityTone} testId="kpi-qc"/>
        <KpiTile icon={ChartLineUp} label="Actual dry" value={k.actual_dry_kg} unit=" kg"
                 sub={`Planned ${k.planned_dry_kg} kg`} testId="kpi-dry"/>
      </div>
    </div>
  );
}
